import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";
import Input from "../common/Input";

interface ChangeUserEmailModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  adminEmail: string;
}

const ChangeUserEmailModal: React.FC<ChangeUserEmailModalProps> = ({
  modalRef,
  adminEmail,
}) => {
  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setCurrentEmail("");
    setNewEmail("");
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    modalRef.current?.close();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(currentEmail) || !emailRegex.test(newEmail)) {
      setError("Both emails must be valid");
      return;
    }
    if (currentEmail.toLowerCase() === newEmail.toLowerCase()) {
      setError("New email must be different from current email");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        "/api/agency-management/users/send-email-change",
        {
          currentEmail: currentEmail.trim(),
          newEmail: newEmail.trim(),
          adminEmail,
        },
        { headers: { "x-admin-email": adminEmail } }
      );
      toast.success(`Invitation sent to ${newEmail}`);
      handleClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to send invitation");
      setSubmitting(false);
    }
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">Change User Email</h3>
          <button
            onClick={handleClose}
            className="btn btn-sm btn-circle btn-ghost"
            type="button"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-base-content/70 mb-4">
          Sends a tokenized link to the new email address. The user clicks the
          link, sets a new password (or links Google if their account is not
          part of an agency), and their account email is updated.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className="font-semibold pb-1 text-sm">Current email</p>
            <Input
              type="email"
              value={currentEmail}
              onChange={(e) => setCurrentEmail(e.target.value)}
              placeholder="user@oldemail.com"
              className="w-full min-w-full"
            />
          </div>
          <div>
            <p className="font-semibold pb-1 text-sm">New email</p>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@newemail.com"
              className="w-full min-w-full"
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </Button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={handleClose}>close</button>
      </form>
    </dialog>
  );
};

export default ChangeUserEmailModal;
